import React, { useState } from 'react';
import styles from './ToggleSlider.module.css';
import ConfirmAction from '../ConfirmAction/ConfirmAction';

interface ToggleSliderProps {
    /** Current state of the toggle */
    isOn: boolean;
    /** Callback when toggle is clicked */
    onToggle: (newState: boolean) => void;
    /** Optional confirmation handler - if provided, will show confirmation before toggling */
    onConfirmAction?: (newState: boolean) => Promise<void>;
    /** Disabled state */
    disabled?: boolean;
    /** Size variant */
    size?: 'small' | 'medium' | 'large';
    /** Additional CSS class */
    className?: string;
    /** Confirmation dialog title */
    confirmTitle?: string;
    /** Confirmation dialog message */
    confirmMessage?: string;
    /** Confirm button text */
    confirmText?: string;
    /** Cancel button text */
    cancelText?: string;
    /** Disable confirmation dialog - toggle immediately without confirmation */
    disableConfirm?: boolean;
}

const ToggleSlider: React.FC<ToggleSliderProps> = ({
    isOn,
    onToggle,
    onConfirmAction,
    disabled = false,
    size = 'medium',
    className = '',
    confirmTitle = 'Confirm Action',
    confirmMessage = 'Are you sure you want to proceed?',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    disableConfirm = false
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const handleToggle = () => {
        if (disabled || isLoading) return;
        
        if (disableConfirm) {
            handleConfirmAction();
        } else {
            setShowConfirm(true);
        }
    };

    const handleConfirmAction = async () => {
        const newState = !isOn;

        if (onConfirmAction) {
            try {
                setIsLoading(true);
                await onConfirmAction(newState);
                setShowConfirm(false);
            } catch (error) {
                console.error('Toggle action failed:', error);
                setShowConfirm(false);
                throw error;
            } finally {
                setIsLoading(false);
            }
        } else {
            onToggle(newState);
            setShowConfirm(false);
        }
    };

    const handleCancel = () => {
        setShowConfirm(false);
    };

    const sizeClass = styles[`size-${size}`];
    const toggleClasses = [
        styles.toggleContainer,
        sizeClass,
        isOn ? styles.on : styles.off,
        disabled ? styles.disabled : '',
        isLoading ? styles.loading : '',
        className
    ].filter(Boolean).join(' ');

    return (
        <>
            <div className={styles.toggleWrapper}>
                <div className={toggleClasses} onClick={handleToggle}>
                    <div className={styles.toggleTrack}>
                        <div className={styles.toggleThumb} />
                    </div>
                </div>
                <span className={`${styles.toggleLabel} ${isOn ? styles.enabled : styles.disabled}`}>
                    {isOn ? 'Enabled' : 'Disabled'}
                </span>
            </div>
            
            <ConfirmAction
                isVisible={showConfirm}
                onClose={handleCancel}
                clickHandler={handleConfirmAction}
                title={confirmTitle}
                message={confirmMessage}
                confirmText={confirmText}
                cancelText={cancelText}
                severity="warning"
                loading={isLoading}
            />
        </>
    );
};

export default ToggleSlider;
